'use client'

import { AnimatePresence, m } from 'framer-motion'
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Code2,
  Cpu,
  type LucideIcon,
  MessageSquare,
  Palette,
  Plus,
  Rocket,
  Search,
  Sparkles,
} from 'lucide-react'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import type { CSSProperties } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'

import ProcessFocusList from '@/components/sections/ProcessFocusList'
import { StackOrbital } from '@/components/sections/StackVariants'
import { getDirectionTheme } from '@/lib/directionTheme'
import { normalizeDirectionTab } from '@/lib/routeAliases'
import type { ServiceDTO } from '@/types/services'

const ICON_BY_SLUG: Record<string, LucideIcon> = {
  development: Code2,
  'ai-agents': Cpu,
  design: Palette,
  smm: MessageSquare,
  marketing: Rocket,
  seo: Search,
}

type ScopeItem =
  | string
  | { title?: string; description?: string; desc?: string; subtitle?: string; text?: string }

const SCOPE_DESC_FALLBACK: Record<string, string> = {
  'аналитика и тз': 'Фиксируем цели, требования и KPI. Готовим структуру и техническое задание.',
  'ui/ux и прототип': 'Проектируем пользовательский путь и создаём кликабельный прототип.',
  'frontend и backend': 'Разрабатываем клиентскую и серверную части, готовим API и бизнес-логику.',
  'интеграции и api': 'Подключаем CRM, платежи, аналитику и внешние сервисы без потери данных.',
  тестирование: 'Проводим функциональные и интеграционные проверки перед релизом.',
  'запуск и поддержка':
    'Выводим проект в прод, подключаем мониторинг и сопровождаем после запуска.',
}

function normalizeScopeKey(value: string) {
  return value.trim().toLowerCase().replace(/\s+/g, ' ')
}

function compactLabel(text: string) {
  return text.split(/\s+/).filter(Boolean).slice(0, 4).join(' ')
}

function getProofLine(benefits: string[] | undefined) {
  if (Array.isArray(benefits) && benefits.length > 0) {
    return benefits.slice(0, 2).map(compactLabel).join(' • ')
  }
  return 'Фокус на конверсии • Запуск под KPI'
}

export default function DirectionsClient({ services }: { services: ServiceDTO[] }) {
  const searchParams = useSearchParams()
  const router = useRouter()
  const initialTab = normalizeDirectionTab(searchParams.get('tab') || searchParams.get('direction'))
  const availableSlugs = useMemo(() => new Set((services || []).map((s) => s.slug)), [services])

  const [activeId, setActiveId] = useState(() => {
    const first = services?.[0]?.slug || ''
    if (!initialTab) return first
    return availableSlugs.has(initialTab) ? initialTab : first
  })
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const [hideStickyFilters, setHideStickyFilters] = useState(false)
  const cardsGridRef = useRef<HTMLDivElement | null>(null)
  const [maxCopyHeight, setMaxCopyHeight] = useState(0)
  const activeData = (services || []).find((d) => d.slug === activeId) || (services || [])[0]
  const activeTheme = getDirectionTheme(activeData?.slug)
  const tabsCount = (services || []).length
  const tabsRemainder = tabsCount % 3

  useEffect(() => {
    const root = cardsGridRef.current
    if (!root) return

    let frame = 0
    const measure = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const blocks = Array.from(root.querySelectorAll<HTMLElement>('[data-card-copy-inner]'))
        if (!blocks.length) return
        const next = blocks.reduce((max, node) => Math.max(max, node.offsetHeight), 0)
        setMaxCopyHeight((prev) => (prev === next ? prev : next))
      })
    }

    measure()
    const observer = new ResizeObserver(() => measure())
    observer.observe(root)
    root
      .querySelectorAll<HTMLElement>('[data-card-copy-inner]')
      .forEach((node) => observer.observe(node))
    window.addEventListener('resize', measure)

    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [activeData?.slug, activeData?.subServices])

  useEffect(() => {
    let lastY = window.scrollY
    let frame = 0

    const onScroll = () => {
      if (frame) return
      frame = window.requestAnimationFrame(() => {
        const currentY = window.scrollY
        const delta = currentY - lastY
        setHideStickyFilters((prev) => {
          const nextHidden =
            currentY < 150 ? false : delta > 4.5 ? true : delta < -2.8 ? false : prev
          return prev === nextHidden ? prev : nextHidden
        })
        lastY = currentY
        frame = 0
      })
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      if (frame) window.cancelAnimationFrame(frame)
      window.removeEventListener('scroll', onScroll)
    }
  }, [])

  const prefetchRoute = (href: string) => {
    try {
      router.prefetch(href)
    } catch {
      // no-op
    }
  }

  if (!activeData) return null

  return (
    <div
      className="relative min-h-screen text-white selection:bg-blue-500/30"
      style={{ '--accent-rgb': activeTheme.accentRgb } as CSSProperties}
    >
      <div className="relative z-10">
        {/* 100VH CINEMATIC HERO */}
        <section className="hero-mobile-safe relative flex min-h-[100svh] flex-col items-center justify-start overflow-hidden px-4 pt-0 pb-6 sm:pb-0 md:justify-center md:pt-16 lg:pt-14 [@media(max-height:820px)]:md:pt-8 [@media(max-height:980px)]:md:pt-12">
          {/* CENTER CONTENT */}
          <div className="home-container relative z-20 text-center">
            <m.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* Decorative Line */}
              <m.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.18, duration: 0.55 }}
                className="mb-5 inline-flex items-center gap-3 rounded-full border border-white/12 bg-white/[0.04] px-6 py-3 backdrop-blur-sm sm:mb-7 [@media(max-height:820px)]:md:mb-4"
              >
                <Sparkles className="h-4 w-4 text-sky-400" />
                <span className="home-meta text-gray-200">Система компетенций</span>
              </m.div>

              <m.div
                initial={{ width: 0 }}
                animate={{ width: '120px' }}
                transition={{ delay: 0.5, duration: 1 }}
                className="mx-auto mb-7 h-[2px] bg-gradient-to-r from-transparent via-purple-500 to-transparent [@media(max-height:820px)]:md:mb-5"
              />

              {/* Main Title */}
              <h1 className="home-hero-title relative mb-6 tracking-tight text-balance [@media(max-height:820px)]:md:mb-4 [@media(max-height:980px)]:md:mb-5">
                <span className="pointer-events-none absolute -inset-8 rounded-full bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 opacity-50 blur-[60px]" />
                <span className="relative z-10 block text-white">Архитектура роста</span>
                <span className="relative z-10 block bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-300 bg-clip-text text-transparent">
                  для сильных брендов
                </span>
              </h1>

              {/* Subtitle */}
              <p className="home-hero-lead mx-auto mb-7 max-w-3xl text-gray-400 sm:mb-8 [@media(max-height:820px)]:md:mb-5">
                Объединяем стратегическое мышление, современный дизайн и передовые технологии.
                Создаём цифровые продукты, которые не просто работают —{' '}
                <span className="text-white">меняют правила игры на рынке</span>.
              </p>

              {/* CTA BUTTONS (Expanded) */}
              <div className="mb-7 flex flex-col flex-wrap justify-center gap-3 sm:mb-12 sm:flex-row sm:gap-6 [@media(max-height:820px)]:md:mb-7">
                <Link
                  href="/contacts"
                  className="group flex w-full items-center justify-center gap-3 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 px-7 py-4 text-base font-semibold text-white transition-all duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(59,130,246,0.4)] sm:w-auto sm:px-10 sm:py-5 sm:text-lg [@media(max-height:980px)]:md:px-8 [@media(max-height:980px)]:md:py-3.5 [@media(max-height:980px)]:md:text-base"
                >
                  Обсудить задачу
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
                <button
                  onClick={() => {
                    const el = document.getElementById('directions-grid')
                    el?.scrollIntoView({ behavior: 'smooth' })
                  }}
                  className="group flex w-full items-center justify-center gap-3 rounded-full border border-white/10 bg-white/5 px-7 py-4 text-base text-white transition-all duration-300 hover:border-white/20 hover:bg-white/10 sm:w-auto sm:px-10 sm:py-5 sm:text-lg [@media(max-height:980px)]:md:px-8 [@media(max-height:980px)]:md:py-3.5 [@media(max-height:980px)]:md:text-base"
                >
                  <Cpu className="h-5 w-5 text-gray-400 transition-colors group-hover:text-white" />
                  Стек технологий
                </button>
              </div>
            </m.div>

            {/* STATS ROW */}
            <m.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="hero-stats-3 flex flex-wrap items-center justify-center gap-7 border-t border-white/5 pt-6 sm:gap-10 sm:pt-8 md:gap-16 [@media(max-height:820px)]:md:gap-10 [@media(max-height:820px)]:md:pt-6"
            >
              {[
                { value: '5', label: 'направлений' },
                { value: '30+', label: 'технологий' },
                { value: 'Полный цикл', label: 'агентство' },
              ].map((stat, i) => (
                <m.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + i * 0.1 }}
                  className="group cursor-default text-center"
                >
                  <div className="hero-stat-value mb-2 bg-gradient-to-b from-white to-white/50 bg-clip-text text-transparent transition-all duration-500 group-hover:to-white">
                    {stat.value}
                  </div>
                  <div className="hero-stat-label text-gray-500 transition-colors group-hover:text-[rgb(var(--accent-rgb))]">
                    {stat.label}
                  </div>
                </m.div>
              ))}
            </m.div>
          </div>

          {/* Scroll Indicator */}
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 1 }}
            className="absolute bottom-9 left-1/2 z-20 hidden -translate-x-1/2 flex-col items-center gap-2 md:flex [@media(max-height:820px)]:hidden"
          >
            <span className="home-meta text-gray-600">Листать</span>
            <m.div
              animate={{ y: [0, 6, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="h-8 w-[1px] bg-gradient-to-b from-white/20 to-transparent"
            />
          </m.div>
        </section>

        {/* NAVIGATION & CONTENT WRAPPER */}
        <div
          id="directions-grid"
          className="home-container relative z-20 pt-12 pb-20 sm:pt-16 sm:pb-32 lg:pt-20 lg:pb-40"
        >
          {/* STICKY NAVIGATION */}
          <m.div
            className={`sticky top-[calc(var(--nav-offset)+0.75rem)] z-40 mb-12 flex justify-start sm:top-[calc(var(--nav-offset)+1rem)] sm:mb-20 md:mb-32 md:justify-center ${
              hideStickyFilters ? 'pointer-events-none' : ''
            }`}
            initial={false}
            animate={
              hideStickyFilters
                ? { y: -18, opacity: 0, scale: 0.985, filter: 'blur(2px)' }
                : { y: 0, opacity: 1, scale: 1, filter: 'blur(0px)' }
            }
            transition={{ type: 'spring', stiffness: 360, damping: 34, mass: 0.75 }}
          >
            {/* Mobile: 2 rows (grid). Tablet/Desktop: single row (flex). */}
            <div className="no-scrollbar grid max-w-full grid-cols-6 justify-start gap-1 overflow-visible rounded-[28px] border border-white/10 bg-[#0c1220]/80 px-2 py-2 shadow-2xl backdrop-blur-xl md:flex md:flex-nowrap md:justify-center md:gap-2 md:overflow-x-auto md:rounded-full md:p-2">
              {(services || []).map((item, idx) => {
                const Icon = ICON_BY_SLUG[item.slug] || Code2
                // Use a 6-col grid on mobile:
                // - default: 3 items per row (each spans 2 columns)
                // - last row with 2 items: each spans 3 columns (centered)
                // - last row with 1 item: spans 6 columns (centered)
                const spanClass =
                  tabsRemainder === 1 && idx === tabsCount - 1
                    ? 'col-span-6'
                    : tabsRemainder === 2 && (idx === tabsCount - 2 || idx === tabsCount - 1)
                      ? 'col-span-3'
                      : 'col-span-2'
                return (
                  <button
                    key={item.slug}
                    onClick={() => {
                      setActiveId(item.slug)
                    }}
                    className={`relative ${spanClass} home-caption group w-full overflow-hidden rounded-full px-4 py-2 text-center leading-tight font-medium whitespace-nowrap transition-all duration-300 md:w-auto md:px-8 md:py-3 md:text-base ${
                      activeId === item.slug
                        ? 'text-white'
                        : 'text-gray-400 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    {activeId === item.slug && (
                      <m.div
                        layoutId="activeTab"
                        className={`absolute inset-0 bg-gradient-to-r ${item.gradient} opacity-20`}
                      />
                    )}
                    {activeId === item.slug && (
                      <m.div
                        layoutId="activeTabBorder"
                        className="absolute inset-0 rounded-full border border-white/20"
                      />
                    )}

                    <span className="relative z-10 flex w-full items-center justify-center gap-2">
                      <Icon
                        className={`hidden h-4 w-4 md:block ${activeId === item.slug ? 'text-white' : 'text-gray-500 group-hover:text-white'}`}
                      />
                      {item.title}
                    </span>
                  </button>
                )
              })}
            </div>
          </m.div>

          {/* DYNAMIC CONTENT AREA */}
          <div className="relative min-h-[500px]">
            <AnimatePresence mode="wait">
              <m.div
                key={activeId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="space-y-16 sm:space-y-24 lg:space-y-32"
              >
                {/* 1. HEADER: Title & Description */}
                <div className="mx-auto max-w-5xl text-center">
                  <div className="home-meta mb-6 inline-block rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-gray-300 md:mb-8">
                    {activeData.subtitle}
                  </div>
                  <h2 className="home-title mb-5 leading-tight sm:mb-6 md:mb-8">
                    {activeData.title}
                  </h2>
                  <div className="home-lead whitespace-pre-wrap text-gray-300">
                    {activeData.description}
                  </div>
                </div>

                {/* 2. SUB-SERVICES: Photo Cards */}
                <div>
                  <h3 className="home-title mb-8 sm:mb-12">Услуги направления</h3>
                  <div
                    ref={cardsGridRef}
                    className="grid grid-cols-1 items-stretch gap-5 md:grid-cols-2 md:gap-6 xl:grid-cols-3"
                  >
                    {activeData.subServices.map((sub, i) => {
                      const href = `/services/${activeData.slug}/${sub.slug}`
                      const proofLine = getProofLine(activeData.benefits)
                      const CardIcon = ICON_BY_SLUG[activeData.slug] || Code2

                      return (
                        <Link
                          key={`${activeData.slug}/${sub.slug}`}
                          href={href}
                          data-testid={`service-card-${activeData.slug}-${sub.slug}`}
                          onMouseEnter={() => prefetchRoute(href)}
                          onFocus={() => prefetchRoute(href)}
                          style={{ '--accent-rgb': activeTheme.accentRgb } as CSSProperties}
                          className="group relative flex h-full flex-col overflow-hidden rounded-[24px] border border-white/14 bg-gradient-to-b from-[#111a34]/72 via-[#0b1223]/92 to-[#090f1d]/98 [box-shadow:0_0_0px_transparent] backdrop-blur-xl transition-all duration-500 hover:-translate-y-1 hover:border-white/30 hover:[box-shadow:0_22px_60px_rgb(var(--accent-rgb)_/_0.22)]"
                        >
                          <m.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 + i * 0.07 }}
                            className="relative flex h-full flex-1 flex-col p-5 md:p-6"
                          >
                            <div className="pointer-events-none absolute top-0 -left-1/3 h-full w-1/3 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 blur-xl transition-all duration-700 group-hover:translate-x-[430%] group-hover:opacity-100" />
                            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[rgb(var(--accent-rgb))] to-transparent opacity-80" />
                            <div className="pointer-events-none absolute -top-24 right-0 h-40 w-40 rounded-full bg-[rgb(var(--accent-rgb))]/12 blur-3xl transition-opacity duration-500 group-hover:opacity-100" />
                            <div className="pointer-events-none absolute bottom-0 -left-12 h-32 w-32 rounded-full bg-[rgb(var(--accent-rgb))]/8 blur-2xl" />

                            <div className="mb-5 flex items-start justify-between">
                              <div className="flex items-center gap-2">
                                <span className="home-meta rounded-full border border-white/14 bg-white/[0.08] px-3 py-1.5 text-[10px] tracking-[0.14em] text-gray-300">
                                  {activeData.title}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="bg-gradient-to-r from-white/75 to-white/25 bg-clip-text text-lg font-bold tracking-[0.08em] text-transparent">
                                  {String(i + 1).padStart(2, '0')}
                                </span>
                                <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/14 bg-white/[0.08] text-gray-300 transition-colors duration-300 group-hover:bg-white/[0.14] group-hover:text-white">
                                  <CardIcon className="h-4 w-4" />
                                </div>
                              </div>
                            </div>

                            <div
                              className="mb-5"
                              data-card-copy
                              style={
                                maxCopyHeight > 0 ? { minHeight: `${maxCopyHeight}px` } : undefined
                              }
                            >
                              <div data-card-copy-inner>
                                <h4 className="home-card-title-sm mb-3 text-white">{sub.title}</h4>
                                <p className="home-subnote leading-relaxed text-gray-300/90">
                                  {sub.desc}
                                </p>
                              </div>
                            </div>

                            <div className="mb-5 max-w-full">
                              <p
                                title={proofLine}
                                className="home-meta inline-flex max-w-full items-start rounded-2xl border border-white/14 bg-white/[0.08] px-3 py-1.5 text-[12px] leading-tight whitespace-normal text-gray-300"
                              >
                                <span className="block max-w-full leading-tight [overflow-wrap:anywhere] break-words whitespace-normal">
                                  {proofLine}
                                </span>
                              </p>
                            </div>

                            <div className="mt-auto flex items-center justify-between border-t border-white/10 pt-3">
                              <span
                                className={`bg-gradient-to-r text-xl font-bold ${activeData.gradient} bg-clip-text text-transparent`}
                              >
                                {sub.price?.trim() || 'по запросу'}
                              </span>
                              <ArrowRight className="h-5 w-5 text-gray-400 transition-all group-hover:translate-x-1 group-hover:text-white" />
                            </div>
                          </m.div>
                        </Link>
                      )
                    })}
                  </div>
                </div>

                {/* 3. SCOPE OF WORK (WHAT'S INCLUDED) */}
                <div>
                  <h3 className="home-title mb-8 text-center sm:mb-12">Что входит в работу</h3>
                  <div className="relative">
                    <div
                      className={`absolute top-1/2 left-1/2 h-64 w-full max-w-4xl -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r ${activeData.gradient} pointer-events-none rounded-full opacity-[0.15] blur-[100px]`}
                    />

                    {/* Grid: Process data prefers 2 cols as requested. */}
                    <div className="relative z-10 grid gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-2 lg:gap-8">
                      {(activeData.process && activeData.process.length > 0
                        ? activeData.process
                        : activeData.scope || []
                      ).map((item: ScopeItem, i: number) => {
                        const title = typeof item === 'string' ? item : item.title || ''
                        const descFromItem =
                          typeof item === 'string'
                            ? null
                            : item.description || item.desc || item.subtitle || item.text || null
                        const descFallback = title
                          ? SCOPE_DESC_FALLBACK[normalizeScopeKey(title)] || null
                          : null
                        const desc = descFromItem || descFallback

                        return (
                          <m.div
                            key={i}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.05 }}
                            className="group relative min-h-[128px] overflow-hidden rounded-[22px] border border-white/5 bg-[#0c1220] p-6 transition-all duration-300 hover:-translate-y-1 hover:border-white/10 hover:shadow-2xl hover:shadow-black/50 sm:min-h-[150px] sm:rounded-[28px] sm:p-8 lg:min-h-[220px] lg:p-10"
                          >
                            <div
                              className={`absolute inset-0 bg-gradient-to-br ${activeData.gradient} opacity-0 transition-opacity duration-500 group-hover:opacity-[0.03]`}
                            />
                            <div
                              className={`absolute top-0 bottom-0 left-0 w-[2px] bg-gradient-to-b ${activeData.gradient} opacity-0 transition-opacity duration-300 group-hover:opacity-100`}
                            />

                            <div className="relative z-10 flex h-full flex-col justify-between">
                              <div>
                                <span className="home-card-title-sm mb-3 block leading-snug text-gray-200 transition-colors group-hover:text-white">
                                  {title}
                                </span>
                                {desc && (
                                  <p className="home-subnote leading-relaxed text-gray-400">
                                    {desc}
                                  </p>
                                )}
                              </div>
                              <div className="pointer-events-none absolute right-3 bottom-2 text-6xl font-black opacity-[0.035] transition-opacity select-none group-hover:opacity-[0.07] sm:text-7xl">
                                0{i + 1}
                              </div>
                            </div>
                            <div
                              className={`absolute top-0 right-0 h-12 w-12 bg-gradient-to-br ${activeData.gradient} opacity-0 blur-xl transition-opacity duration-500 group-hover:opacity-10`}
                            />
                          </m.div>
                        )
                      })}
                    </div>
                  </div>
                </div>

                {/* 4. BUSINESS BENEFITS */}
                <div className="relative grid items-center gap-10 overflow-hidden rounded-[32px] border border-white/10 bg-[#0c1220] p-7 sm:gap-12 sm:rounded-[40px] sm:p-10 md:grid-cols-2 lg:gap-14 lg:p-14">
                  <div
                    className={`absolute top-0 right-0 h-[500px] w-[500px] bg-gradient-to-br ${activeData.gradient} pointer-events-none opacity-[0.05] blur-[150px]`}
                  />
                  <div className="relative z-10">
                    <h3 className="home-title mb-6 sm:mb-8">Зачем это бизнесу</h3>
                    <div className="space-y-6">
                      {activeData.benefits.map((benefit, i) => (
                        <m.div
                          key={i}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.1 }}
                          className="flex items-start gap-4"
                        >
                          <CheckCircle2 className="mt-1 h-6 w-6 text-white sm:h-7 sm:w-7" />
                          <span className="home-lead leading-snug font-medium text-gray-200">
                            {benefit}
                          </span>
                        </m.div>
                      ))}
                    </div>
                  </div>
                  {/* FAQ */}
                  <div className="relative z-10 space-y-4">
                    {activeData.faq.map((item, i) => (
                      <div
                        key={i}
                        className="overflow-hidden rounded-2xl border border-white/5 bg-white/[0.02]"
                      >
                        <button
                          onClick={() => setOpenFaq(openFaq === i ? null : i)}
                          className="flex w-full items-center justify-between p-4 text-left transition-colors hover:bg-white/5 sm:p-6 lg:p-7"
                        >
                          <span className="home-body pr-4 leading-snug font-semibold">
                            {item.q}
                          </span>
                          <Plus
                            className={`h-5 w-5 shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-45' : ''}`}
                          />
                        </button>
                        <AnimatePresence>
                          {openFaq === i && (
                            <m.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="home-subnote border-t border-white/5 p-4 pt-0 leading-relaxed text-gray-400 sm:p-6 lg:p-7">
                                {item.a}
                              </div>
                            </m.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 5. TECH STACK */}
                <div>
                  <h3 className="home-title mb-8 text-center sm:mb-12">Технологический стек</h3>
                  <div className="relative">
                    <StackOrbital
                      stack={(activeData.stack || []).slice(0, 12)}
                      gradient={activeData.gradient}
                      compact
                      transparent
                    />
                  </div>
                </div>

                {/* 6. PROCESS (FOCUS ITEM) - Displaying 'Approach' data */}
                {activeData.approach && activeData.approach.length > 0 && (
                  <ProcessFocusList process={activeData.approach} gradient={activeData.gradient} />
                )}

                {/* 7. CTA */}
                <div className="flex justify-center pb-10 sm:pb-20">
                  <Link
                    href="/contacts"
                    className={`group inline-flex items-center gap-4 rounded-full bg-gradient-to-r px-8 py-4 sm:px-12 sm:py-6 ${activeData.gradient} text-base font-bold text-white shadow-2xl transition-all duration-300 hover:scale-105 sm:text-xl`}
                  >
                    Обсудить проект
                    <ArrowUpRight className="h-7 w-7 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </Link>
                </div>
              </m.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  )
}
